import { useState } from "react";
import type { ReactNode } from "react";
import { Icon } from "@iconify/react";
import { Input, Dropdown } from "antd";
import { Button } from "@/shared/components/button";
import { cn } from "@/shared/lib/utils";
import { NotificationsMenu } from "@/shared/layout/NotificationsMenu";

export interface HeaderMenuItem {
  label: string;
  onClick: () => void;
  danger?: boolean;
}

interface PortalHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  avatarSrc?: string | null;
  avatarInitials: string;
  menuItems: HeaderMenuItem[];
  searchPlaceholder?: string;
  showNotifications?: boolean;
  /** Which portal's notification feed the bell shows (default: patient/caregiver). */
  notificationsVariant?: "patient" | "physician" | "admin";
}

export function PortalHeader({
  title,
  description,
  avatarSrc,
  avatarInitials,
  menuItems,
  searchPlaceholder = "Search anything...",
  showNotifications = false,
  notificationsVariant = "patient",
}: PortalHeaderProps) {
  const [searchValue, setSearchValue] = useState("");

  const menuContent = (
    <div className="w-[150px] overflow-hidden rounded-lg border border-[#DADDE5] bg-white shadow-[0_4px_12px_rgba(17,24,39,0.14)]">
      {menuItems.map((item, index) => (
        <div key={item.label}>
          {index > 0 && <div className="h-px bg-[#E5E7EB]" />}
          <Button
            variant="ghost"
            onClick={item.onClick}
            className={cn(
              "!block !h-auto !w-full !justify-start !rounded-none !px-4 !py-3 !text-left !text-sm !font-medium",
              item.danger ? "!text-[#E4002B] hover:!bg-[#FFF2F4] hover:!text-[#E4002B]" : "!text-[#180047] hover:!bg-[#F7F7F7] hover:!text-[#180047]",
            )}
          >
            {item.label}
          </Button>
        </div>
      ))}
    </div>
  );

  return (
    <header className="flex h-[73px] w-full shrink-0 items-center justify-between border-b border-[#E5E7EB] bg-[#F7F7F7] px-6">
      {/* Left — page title + description */}
      <div className="flex flex-col justify-center gap-0">
        <h2 className="text-lg tracking-tight text-[#111827]" style={{ fontWeight: 900, letterSpacing: "-0.02em" }}>
          {title}
        </h2>
        {description && <p className="text-[11px] leading-none text-[#9CA3AF]">{description}</p>}
      </div>

      {/* Right — Search + Bell + Avatar */}
      <div className="flex items-center gap-3">
        <Input
          prefix={<Icon icon="lucide:search" className="h-4 w-4 text-[#4B5563]" />}
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          className="!h-8 !w-64 !rounded-full !border-black !bg-white !text-sm !text-[#4B5563] placeholder:!text-[#4B5563]"
        />

      
        {showNotifications && <NotificationsMenu variant={notificationsVariant} />}

        <Dropdown
          trigger={["click"]}
          popupRender={() => menuContent}
          placement="bottomRight"
        >
          <Button
            variant="ghost"
            aria-label="Open profile menu"
            className="!flex !h-9 !w-9 !items-center !justify-center !rounded-full !bg-[#180047] !p-0 !text-center !text-xs !font-bold !leading-none !text-white hover:!bg-[#180047] hover:!opacity-85"
          >
            {avatarSrc ? (
              <img src={avatarSrc} alt="" className="h-full w-full rounded-full object-cover" />
            ) : (
              <span className="block leading-none !text-white">{avatarInitials}</span>
            )}
          </Button>
        </Dropdown>
      </div>
    </header>
  );
}
